import { TrayFormViewModelType } from '../../create-tray-screen/components/view-model-common/view-model-type';
import {
  BannersWithCTALayout,
  CarouselLayout,
  DomainLayout,
  GenericLayout,
  HeroLayout,
  MarketingItemLayout,
  StudiosTrayLayout,
} from '../../domain/domain-layouts';
import { PageVersion, PageVersionItem, PageVersionItemType } from '../../domain/page-version';
import {
  PageEditScreenViewModel,
  PageEditTrayItemViewModel,
  PageEditTrayLayout,
} from '../components/page-edit-screen/page-edit-screen.view-model';

/* eslint-disable prefer-arrow/prefer-arrow-functions */

export function mapDomainPageVersionToVm(pageVersion: PageVersion): PageEditScreenViewModel {
  return {
    pageId: pageVersion.id,
    pageUrl: pageVersion.slug,
    draftPageVersionId: pageVersion.draftPageId,
    publishedPageVersionId: pageVersion.livePageId,
    countryIds: pageVersion.type === 'Redeem' ? pageVersion.countries.map((it) => it.id) : undefined,
    attachedGenre: pageVersion.type === 'Genre' ? pageVersion.genre : undefined,
    internalName: pageVersion.name,
    titleEnglish: pageVersion.englishTitle,
    userSegmentIds: pageVersion.type === 'Redeem' ? pageVersion.userSegments.map((it) => it.id) : undefined,
    trays: pageVersion.pageItems.map(mapDomainPageVersionItemToVm),
    titleArabic: pageVersion.arabicTitle,
    metaTags: { ...pageVersion.metaTags },
    SEODescription: pageVersion.seoDescription,
    SEOTitle: pageVersion.seoTitle,
  };
}

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function mapDomainPageVersionItemToVm(item: PageVersionItem): PageEditTrayItemViewModel {
  const baseProps: PageEditTrayItemViewModel = {
    id: item.id,
    name: item.name,
    countries: item.countries.map((it) => it.name),
    layout: layoutRecord[item.layout ?? item.trayType],
    scheduledTo: item.scheduledTo,
    scheduledFrom: item.scheduledFrom,
    allCountriesSelected: false,
    draggable: item.draggable,
    order: item.order,
    trayType: trayTypeRecord[item.trayType],
    userSegments: item.userSegments.map((it) => it.name),
    visible: item.visible,
    platforms: item.platforms.map((it) => it.name.toLowerCase()),
  };

  switch (item.trayType) {
    case 'PortraitPageItem':
    case 'TopTenPageItem':
    case 'ExpandablePageItem':
    case 'CollectionPageItem':
    case 'StreamingContentTray':
    case 'BannersTrayWithCta':
    case 'TopTenOnOSNPlusPosterArtTray':
    case 'FullscreenTray':
    case 'SearchTagsTray':
      return {
        ...baseProps,
        feedId: item.relatedFeed?.id,
        feedName: item.relatedFeed?.title,
      };
    case 'LandscapeTray':
      return {
        ...baseProps,
        contentGroupId: item.contentGroup?.id,
        contentGroupName: item.contentGroup?.title,
        contentGroupPath: item.contentGroup?.contentGroupPath,
      };
    case 'AppsAndGamesInstalledApps':
    case 'AppsAndGamesRecommendedApps':
      return {
        ...baseProps,
        subType: item.trayType,
      };
    case 'CarouselBannerTray':
    case 'CarouselTray':
      return {
        ...baseProps,
        contentGroupId: item.contentGroup?.id,
        contentGroupName: item.contentGroup?.title,
        contentGroupPath: item.contentGroup?.contentGroupPath,
        feedId: item.relatedFeed?.id,
        feedName: item.relatedFeed?.title,
      };
    default:
      return baseProps;
  }
}

const trayTypeRecord: Record<PageVersionItemType, TrayFormViewModelType> = {
  // CXB
  AppLandingCotvPageItem: TrayFormViewModelType.AppLandingCotvTray,
  AppLandingMobilePageItem: TrayFormViewModelType.AppLandingMobileTray,
  CollectionPageItem: TrayFormViewModelType.CollectionTray,
  ContinueWatchingCarouselPageItem: TrayFormViewModelType.ContinueWatchingCarousel, // CXB
  WatchlistPageItem: TrayFormViewModelType.WatchlistCarousel, // CXB
  DetailedPageMarketingBannerPageItem: TrayFormViewModelType.DetailedPageMarketingBanner,
  DeviceModulePageItem: TrayFormViewModelType.DeviceModuleTray,
  EmailCollectorPageItem: TrayFormViewModelType.EMAIL,
  ExpandablePageItem: TrayFormViewModelType.ExpandableTray,
  FaqPageItem: TrayFormViewModelType.FaqTray,
  GenrePageItem: TrayFormViewModelType.GenreTray,
  HeroPageItem: TrayFormViewModelType.HeroTray,
  MarketingEmailBlockerPageItem: TrayFormViewModelType.MarketingEmailBlocker,
  MarketingCtaPageItem: TrayFormViewModelType.CTA,
  MarketingHeroPageItem: TrayFormViewModelType.MarketingHero,
  MarketingKidsPageItem: TrayFormViewModelType.KIDS,
  MarketingStudioPageItem: TrayFormViewModelType.STUDIO,
  MarketingTrendingPageItem: TrayFormViewModelType.TRENDING,
  MarketingUspPageItem: TrayFormViewModelType.USP,
  PortraitPageItem: TrayFormViewModelType.PortraitTray,
  RecommendationBecauseYouWatchedPageItem: TrayFormViewModelType.BecauseYouWatched,
  RecommendationBecauseYouWatchGenrePageItem: TrayFormViewModelType.BecauseYouWatchGenreTray,
  RecommendationPeopleLikeYouWatchPageItem: TrayFormViewModelType.PeopleLikeYouWatch,
  RecommendationPeopleLikeYouWatchMoviesPageItem: TrayFormViewModelType.PeopleLikeYouWatchMovies,
  RecommendationPeopleLikeYouWatchSeriesPageItem: TrayFormViewModelType.PeopleLikeYouWatchSeries,
  RecommendationTopPicksPageItem: TrayFormViewModelType.TopPicks,
  RecommendationTopPicksMoviesPageItem: TrayFormViewModelType.TopPicksMovies,
  RecommendationTopPicksSeriesPageItem: TrayFormViewModelType.TopPicksSeries,
  RecommendationTrendingPageItem: TrayFormViewModelType.Trending,
  RecommendationTrendingWithLayoutPageItem: TrayFormViewModelType.TrendingWithLayout,
  RecommendationTrendingMoviesPageItem: TrayFormViewModelType.TrendingMovies,
  RecommendationTrendingSeriesPageItem: TrayFormViewModelType.TrendingSeries,
  RecommendationDontMissPageItem: TrayFormViewModelType.DontMiss,
  RedeemCtaPageItem: TrayFormViewModelType.RedeemCtaTray,
  RedeemGenericVoucherPageItem: TrayFormViewModelType.RedeemGenericVoucherTray,
  RedeemPartnerVoucherPageItem: TrayFormViewModelType.RedeemPartnerVoucherTray,
  RedeemUspPageItem: TrayFormViewModelType.RedeemUspTray,
  SeasonPageItem: TrayFormViewModelType.SeasonTray,
  SinglePhotoSeasonPageItem: TrayFormViewModelType.SinglePhotoSeasonTray,
  SpotlightPageItem: TrayFormViewModelType.SpotlightTray,
  StudiosPageItem: TrayFormViewModelType.StudiosTray,
  TopTenPageItem: TrayFormViewModelType.TopTenTray,
  RecommendationTopPicksDownloadablePageItem: TrayFormViewModelType.TopPicksDownloadable,
  RecommendationTopPicksDownloadableMoviesPageItem: TrayFormViewModelType.TopPicksMoviesDownloadable,
  RecommendationTopPicksDownloadableSeriesPageItem: TrayFormViewModelType.TopPicksSeriesDownloadable,
  RecommendationPeopleLikeYouWatchDownloadablePageItem: TrayFormViewModelType.PeopleLikeYouWatchDownloadable,
  RecommendationPeopleLikeYouWatchDownloadableMoviesPageItem: TrayFormViewModelType.PeopleLikeYouWatchMoviesDownloadable,
  RecommendationPeopleLikeYouWatchDownloadableSeriesPageItem: TrayFormViewModelType.PeopleLikeYouWatchSeriesDownloadable,
  RecommendationMostDownloadedMoviesPageItem: TrayFormViewModelType.MostDownloadedMovies,
  RecommendationMostDownloadedSeriesPageItem: TrayFormViewModelType.MostDownloadedSeries,
  RecommendationEditorialPageItem: TrayFormViewModelType.Editorial,
  // UXB
  AppsAndGamesInstalledApps: TrayFormViewModelType.AppsAndGames,
  AppsAndGamesRecommendedApps: TrayFormViewModelType.AppsAndGames,
  RecentlyWatchedTV: TrayFormViewModelType.RecentlyWatchedTV,
  TitleImagePortraitTray: TrayFormViewModelType.TitleImagePortraitTray,
  TopTenOnOSNPlusPosterArtTray: TrayFormViewModelType.TopTenOnOSNPlusPosterArt,
  HeroSpotlight: TrayFormViewModelType.HeroSpotlightTray,
  SearchTagsTray: TrayFormViewModelType.SearchTagsTray,
  StreamingContentTray: TrayFormViewModelType.StreamingContentTray,
  CurrentlyOnTvTray: TrayFormViewModelType.CurrentlyOnTvTray,
  ChannelLogoTray: TrayFormViewModelType.ChannelLogoTray,
  TopTenOnLiveTvTray: TrayFormViewModelType.TopTenOnLiveTvTray,
  ComingSoonForLiveTvTray: TrayFormViewModelType.ComingSoonForLiveTvTray,
  FullscreenTray: TrayFormViewModelType.FullscreenTray,
  BannersTrayWithCta: TrayFormViewModelType.BannersTrayWithCta,
  CarouselTray: TrayFormViewModelType.BannersThree, //AXB-74
  CarouselBannerTray: TrayFormViewModelType.CarouselBannerTray,
  UpcomingOnSpecificTvChannelTray: TrayFormViewModelType.UpcomingOnSpecificTvChannelTray,
  RecommendedChannelsTray: TrayFormViewModelType.RecommendedChannelsTray,
  MarketingBannerTray: TrayFormViewModelType.MarketingBannerTray,
  FavouriteChannels: TrayFormViewModelType.MyFavouritesOnLiveTV,
  YourReminders: TrayFormViewModelType.LiveTVReminders,
  ContinueWatching: TrayFormViewModelType.ContinueWatchingTray, // UXB
  Watchlist: TrayFormViewModelType.MyListTray, // UXB
  TVGuide: TrayFormViewModelType.TVGuide,
  CatchUpTray: TrayFormViewModelType.CatchUpTray,
  ChannelSpecificCatchUpTray: TrayFormViewModelType.ChannelSpecificCatchUpTray,
  MarketingVideoTray: TrayFormViewModelType.MarketingVideoTray,
  MarketingBarTray: TrayFormViewModelType.MarketingBarTray,
  LandscapeTray: TrayFormViewModelType.LandscapeTray,
  WatchlistOnDemandTray: TrayFormViewModelType.WatchlistOnDemandTray,
  PrimeVideoRecommendationsTray: TrayFormViewModelType.PrimeVideoRecommendationsTray,
  OndemandCategoryTray: TrayFormViewModelType.OndemandCategoryTray,
  OndemandBannerTray: TrayFormViewModelType.OndemandBannerTray,
  ContinueWatchingOnDemand: TrayFormViewModelType.ContinueWatchingOndemandTray,
  RecentlyPurchasedTray: TrayFormViewModelType.RecentlyPurchasedTray,
  MixedSearchTagTray: TrayFormViewModelType.MixedSearchTagTray,
  ExpiringStoreContents: TrayFormViewModelType.ExpiringStoreContents,
};

const layoutRecord: Partial<Record<PageVersionItemType, PageEditTrayLayout>> & Record<DomainLayout, PageEditTrayLayout> = {
  // Page version item type members should be the first!!!!
  HeroPageItem: PageEditTrayLayout.Hero, // AXB-980
  ContinueWatchingCarouselPageItem: PageEditTrayLayout.Landscape, // CXB
  WatchlistPageItem: PageEditTrayLayout.Landscape, // CXB
  GenrePageItem: PageEditTrayLayout.Branded,
  MarketingHeroPageItem: PageEditTrayLayout.Hero,
  FaqPageItem: PageEditTrayLayout.MarketingEmailCollectorCentered,
  MarketingEmailBlockerPageItem: PageEditTrayLayout.MarketingEmailBlocker,
  SpotlightPageItem: PageEditTrayLayout.MarketingSpotlightTray,
  DeviceModulePageItem: PageEditTrayLayout.MarketingDeviceModule,
  MarketingVideoTray: PageEditTrayLayout.MarketingVideoTray,
  RedeemGenericVoucherPageItem: PageEditTrayLayout.Hero,
  RedeemPartnerVoucherPageItem: PageEditTrayLayout.Hero,
  // Page version item type members should be the first!!!!
  [CarouselLayout.Portrait]: PageEditTrayLayout.Portrait,
  [CarouselLayout.Landscape]: PageEditTrayLayout.Landscape,
  [CarouselLayout.Collection]: PageEditTrayLayout.Collection,
  [CarouselLayout.Top10]: PageEditTrayLayout.Top10,
  [CarouselLayout.Expandable]: PageEditTrayLayout.Expandable,
  [StudiosTrayLayout.Small]: PageEditTrayLayout.StudiosSmall,
  [StudiosTrayLayout.Medium]: PageEditTrayLayout.StudiosMedium,
  [StudiosTrayLayout.Large]: PageEditTrayLayout.StudiosLarge,
  [HeroLayout.HERO]: PageEditTrayLayout.Hero,
  [MarketingItemLayout.CTA_LEFT]: PageEditTrayLayout.MarketingCTALeft,
  [MarketingItemLayout.CTA_RIGHT]: PageEditTrayLayout.MarketingCTARight,
  [MarketingItemLayout.EMAIL_CENTERED]: PageEditTrayLayout.MarketingEmailCollectorCentered,
  [MarketingItemLayout.EMAIL_LEFT]: PageEditTrayLayout.MarketingEmailCollectorLeft,
  [MarketingItemLayout.EMAIL_RIGHT]: PageEditTrayLayout.MarketingEmailCollectorRight,
  [MarketingItemLayout.MULTIPLE_CONTENT_LANDSCAPE]: PageEditTrayLayout.MarketingMultipleContentLandscape,
  [MarketingItemLayout.MULTIPLE_CONTENT_PORTRAIT]: PageEditTrayLayout.MarketingMultipleContentPortrait,
  [MarketingItemLayout.TRENDING_LEFT]: PageEditTrayLayout.MarketingTrendingLeft,
  [MarketingItemLayout.TRENDING_RIGHT]: PageEditTrayLayout.MarketingTrendingRight,
  [MarketingItemLayout.DETAILED_PAGE_BANNER_LEFT]: PageEditTrayLayout.MarketingCTALeft,
  [MarketingItemLayout.DETAILED_PAGE_BANNER_RIGHT]: PageEditTrayLayout.MarketingCTARight,
  [MarketingItemLayout.DETAILED_PAGE_BANNER_CENTER]: PageEditTrayLayout.MarketingEmailCollectorCentered,
  // UXB (fixed ones)
  AppsAndGamesInstalledApps: PageEditTrayLayout.StudiosSmall,
  AppsAndGamesRecommendedApps: PageEditTrayLayout.TrendingOnAppTray,
  RecentlyWatchedTV: PageEditTrayLayout.StudiosSmall,
  TitleImagePortraitTray: PageEditTrayLayout.TrendingOnAppTray,
  TopTenOnOSNPlusPosterArtTray: PageEditTrayLayout.TopTenOnOSNPlusPosterArtTray,
  HeroSpotlight: PageEditTrayLayout.Hero, // AXB-67, AXB-68
  SearchTagsTray: PageEditTrayLayout.SearchTagsTray,
  ChannelLogoTray: PageEditTrayLayout.StudiosSmall,
  TopTenOnLiveTvTray: PageEditTrayLayout.TopTenOnLiveTvTray,
  FullscreenTray: PageEditTrayLayout.BannersOnlyTray,
  CarouselTray: PageEditTrayLayout.LandscapePostersTray,
  CarouselBannerTray: PageEditTrayLayout.CarouselBannerTray,
  RecommendedChannelsTray: PageEditTrayLayout.StudiosSmall,
  MarketingBannerTray: PageEditTrayLayout.MarketingBannerTray,
  FavouriteChannels: PageEditTrayLayout.StudiosSmall,
  YourReminders: PageEditTrayLayout.StudiosSmall,
  ContinueWatching: PageEditTrayLayout.ContinueWatchingTray, // UXB
  Watchlist: PageEditTrayLayout.StudiosSmall, // UXB
  TVGuide: PageEditTrayLayout.StudiosSmall,
  RecommendationBecauseYouWatchedPageItem: PageEditTrayLayout.Portrait, // AXB-925
  RecommendationBecauseYouWatchGenrePageItem: PageEditTrayLayout.Portrait, // AXB-932
  RecommendationTopPicksPageItem: PageEditTrayLayout.Portrait, // AXB-1061
  RecommendationTopPicksMoviesPageItem: PageEditTrayLayout.Portrait, // AXB-1062
  RecommendationTopPicksSeriesPageItem: PageEditTrayLayout.Portrait, // AXB-924
  RecommendationDontMissPageItem: PageEditTrayLayout.Portrait, // AXB-933
  RecommendationTrendingPageItem: PageEditTrayLayout.Portrait, // AXB-929
  RecommendationTrendingMoviesPageItem: PageEditTrayLayout.Portrait, // AXB-930
  RecommendationTrendingSeriesPageItem: PageEditTrayLayout.Portrait, // AXB-931
  RecommendationPeopleLikeYouWatchPageItem: PageEditTrayLayout.Portrait, // AXB-926
  RecommendationPeopleLikeYouWatchMoviesPageItem: PageEditTrayLayout.Portrait, // AXB-927
  RecommendationPeopleLikeYouWatchSeriesPageItem: PageEditTrayLayout.Portrait, // AXB-928
  // UXB (ones that are selectable by the user)
  // #### (PageEditTrayLayout.LandscapeTwoCardsTray, PageEditTrayLayout.LandscapeThreeCardsTray, PageEditTrayLayout.LandscapeFourCardsTray)
  // StreamingContentTray
  // CurrentlyOnTvTray
  // ComingSoonForLiveTvTray
  // UpcomingOnSpecificTvChannelTray
  // #### (PageEditTrayLayout.MarketingCTAThreeCard)
  // BannersTrayWithCta
  [GenericLayout.TwoCards]: PageEditTrayLayout.LandscapeTwoCardsTray,
  [GenericLayout.ThreeCards]: PageEditTrayLayout.LandscapeThreeCardsTray,
  [GenericLayout.FourCards]: PageEditTrayLayout.LandscapeFourCardsTray,
  [BannersWithCTALayout.TwoCards]: PageEditTrayLayout.MarketingCTATwoCard,
  [BannersWithCTALayout.ThreeCards]: PageEditTrayLayout.MarketingCTAThreeCard,
};
